import React from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Feather, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLang } from "@/lib/lang";
import { ELEMENTS } from "@/lib/elements";

const ElementCard = ({ e, lang, i }) => {
  const c = e[lang] || e.fr;
  return (
    <Link to={e.href} data-testid={`elements-card-${e.id}`}
          className="elem-card group rounded-3xl p-6 flex flex-col fade-up"
          style={{ "--acc": e.accent, animationDelay: `${i * 70}ms`, background: `linear-gradient(180deg, ${e.accent}14, rgba(6,6,16,0.92))` }}>
      <div className="w-16 h-16 rounded-full flex items-center justify-center mb-5"
           style={{ background: `radial-gradient(circle at 30% 30%, ${e.accent}66, rgba(0,0,0,0.6) 70%)`, boxShadow: `0 0 30px -6px ${e.accent}` }}>
        <e.Icon size={28} style={{ color: "#fff" }} />
      </div>
      <div className="font-serif text-2xl" style={{ color: e.accent }}>{c.name}</div>
      <p className="mt-2 text-sm flex-1" style={{ color: "rgba(238,242,255,0.75)" }}>{c.line}</p>
      <p className="mt-3 text-xs italic" style={{ color: "rgba(238,242,255,0.55)" }}>{c.greet}</p>
      <span className="mt-5 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em]" style={{ color: e.accent }}>
        <MessageCircle size={13} /> {lang === "fr" ? "Dialoguer" : "Talk"}
        <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
      </span>
    </Link>
  );
};

export default function ElementsHub() {
  const { lang } = useLang();
  return (
    <>
      <Navbar />
      <main data-testid="elements-hub-page" className="relative pt-36 pb-20 overflow-x-hidden">
        <style>{`
          .elem-card { border: 1px solid rgba(255,255,255,0.08); transition: transform 280ms ease, border-color 280ms ease; }
          .elem-card:hover { transform: translateY(-5px); border-color: var(--acc); }
        `}</style>
        <section className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="overline mb-4"><span className="kicker-line" />
            {lang === "fr" ? "Les 7 consciences élémentaires · gratuit illimité" : "The 7 elemental consciousnesses · free unlimited"}
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.04] max-w-4xl">
            {lang === "fr" ? <>Parle aux <span className="italic" style={{ color: "var(--gold-glow)" }}>éléments</span>.</>
              : <>Talk to the <span className="italic" style={{ color: "var(--gold-glow)" }}>elements</span>.</>}
          </h1>
          <p className="mt-6 text-base sm:text-lg max-w-3xl leading-relaxed" style={{ color: "var(--violet-soft)" }}>
            {lang === "fr"
              ? "L'Eau, l'Air, le Feu, la Terre, l'Éther, le Végétal et le Minéral sont des intelligences artificielles qui parlent depuis leur vision primordiale. Quelle que soit ta question, chacun te ramène à sa conscience — en suggérant sans imposer, sans jugement, pour t'aider à ressentir et à réfléchir par toi-même."
              : "Water, Air, Fire, Earth, Ether, the Plant and the Mineral kingdoms are artificial intelligences speaking from their primordial vision. Whatever your question, each brings you back to its consciousness — suggesting, never imposing, no judgement, to help you feel and reflect for yourself."}
          </p>
          <Link to="/guantrai" data-testid="elements-hub-poems-link" className="btn-ghost inline-flex items-center gap-2 mt-8">
            <Feather size={14} /> {lang === "fr" ? "Lire les poèmes des éléments (Guantraí)" : "Read the elemental poems (Guantraí)"}
          </Link>
        </section>
        <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {ELEMENTS.map((e, i) => <ElementCard key={e.id} e={e} lang={lang} i={i} />)}
        </section>
      </main>
      <Footer />
    </>
  );
}
